// src/pages/Calendar.js
import React, { useState, useEffect, useCallback } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Space,
  Tag,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  ImportOutlined
} from '@ant-design/icons';
import { calendarAPI, articlesAPI } from '../utils/api';
import CalendarImportModal from '../components/CalendarImportModal';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Configurar moment en español
moment.locale('es');
const localizer = momentLocalizer(moment);

// Mensajes en español para el calendario
const messages = {
  allDay: 'Todo el día',
  previous: 'Anterior',
  next: 'Siguiente',
  today: 'Hoy',
  month: 'Mes',
  week: 'Semana',
  day: 'Día',
  agenda: 'Agenda',
  date: 'Fecha',
  time: 'Hora',
  event: 'Evento',
  noEventsInRange: 'No hay eventos en este rango',
  showMore: (total) => `+ Ver más (${total})`
};

const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [, setSelectedDate] = useState(null);
  const [form] = Form.useForm();
  const [articles, setArticles] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    eventType: 'all'
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [importOpen, setImportOpen] = useState(false);

  // Cargar eventos y estadísticas
  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsResponse, statsResponse] = await Promise.all([
        calendarAPI.getEvents(),
        calendarAPI.getStats()
      ]);

      if (eventsResponse.success) {
        // Convertir eventos para react-big-calendar.
        // Los recurrentes se expanden en cliente para que la vista de calendario
        // muestre proyecciones en años pasados/futuros. Click sobre una copia
        // sintética edita el registro original (mismo id, resource preservado).
        const nowYear = new Date().getFullYear();
        const yearFrom = nowYear - 2;
        const yearTo = nowYear + 5;

        const formattedEvents = eventsResponse.data.flatMap(event => {
          const original = new Date(event.date);

          if (!event.recurring) {
            return [{
              id: event.id,
              title: event.title,
              start: original,
              end: original,
              allDay: true,
              resource: event,
              eventType: event.event_type,
              color: event.color
            }];
          }

          const month = original.getMonth();
          const day = original.getDate();
          const copies = [];
          for (let y = yearFrom; y <= yearTo; y++) {
            const projected = new Date(y, month, day);
            copies.push({
              id: `${event.id}_${y}`,   // clave única para react-big-calendar
              title: event.title,
              start: projected,
              end: projected,
              allDay: true,
              resource: event,           // resource conserva el id/datos originales
              eventType: event.event_type,
              color: event.color
            });
          }
          return copies;
        });

        setAllEvents(formattedEvents);
        setEvents(formattedEvents);
      }

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }

      message.success('Eventos cargados correctamente');
    } catch (error) {
      console.error('Error cargando eventos:', error);
      message.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  }, []);

  // Aplicar filtros
  useEffect(() => {
    let filtered = [...allEvents];

    // Filtrar por tipo
    if (filters.eventType !== 'all') {
      filtered = filtered.filter(event => event.eventType === filters.eventType);
    }

    // Filtrar por búsqueda
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchLower) ||
        event.resource.description?.toLowerCase().includes(searchLower)
      );
      
      // Si hay resultados y búsqueda activa, ir al primer evento
      if (filtered.length > 0) {
        setCurrentDate(filtered[0].start);
      }
    }

    setEvents(filtered);
  }, [filters, allEvents]);

  // Cargar artículos de "Te interesa" para vincular
  const loadArticles = useCallback(async () => {
    try {
      const response = await articlesAPI.getArticles('Te interesa', 100);
      if (response.success) {
        setArticles(response.data);
      }
    } catch (error) {
      console.error('Error cargando artículos:', error);
    }
  }, []);

  useEffect(() => {
    loadEvents();
    loadArticles();
  }, [loadEvents, loadArticles]);

  // Abrir modal para crear evento
  const handleSelectSlot = ({ start }) => {
    setSelectedDate(start);
    setEditingEvent(null);
    form.resetFields();
    form.setFieldsValue({
      date: dayjs(start),
      event_type: 'santoral',
      color: 'blue',
      recurring: true
    });
    setModalVisible(true);
  };

  // Abrir modal para editar evento
  const handleSelectEvent = (event) => {
    setEditingEvent(event.resource);
    form.setFieldsValue({
      title: event.resource.title,
      description: event.resource.description,
      date: dayjs(event.resource.date),
      event_type: event.resource.event_type,
      color: event.resource.color,
      recurring: event.resource.recurring,
      article_id: event.resource.article_id
    });
    setModalVisible(true);
  };

  // Guardar evento (crear o actualizar)
  const handleSaveEvent = async (values) => {
    try {
      const eventData = {
        title: values.title,
        description: values.description || null,
        date: values.date.format('YYYY-MM-DD'),
        event_type: values.event_type,
        color: values.color,
        recurring: values.recurring !== false,
        article_id: values.article_id || null
      };

      if (editingEvent) {
        // Actualizar
        await calendarAPI.updateEvent(editingEvent.id, eventData);
        message.success('Evento actualizado correctamente');
      } else {
        // Crear
        await calendarAPI.createEvent(eventData);
        message.success('Evento creado correctamente');
      }

      setModalVisible(false);
      form.resetFields();
      loadEvents();
    } catch (error) {
      console.error('Error guardando evento:', error);
      if (error.response?.status === 409) {
        message.error('Ya existe un evento con esa fecha, tipo y título');
      } else {
        message.error('Error al guardar evento');
      }
    }
  };

  // Eliminar evento
  const handleDeleteEvent = async () => {
    try {
      await calendarAPI.deleteEvent(editingEvent.id);
      message.success('Evento eliminado correctamente');
      setModalVisible(false);
      loadEvents();
    } catch (error) {
      console.error('Error eliminando evento:', error);
      message.error('Error al eliminar evento');
    }
  };

  // Estilos personalizados para eventos
  const eventStyleGetter = (event) => {
    let backgroundColor = '#3174ad';
    
    switch (event.color) {
      case 'green':
        backgroundColor = '#52c41a';
        break;
      case 'red':
        backgroundColor = '#f5222d';
        break;
      case 'blue':
        backgroundColor = '#1890ff';
        break;
      default:
        backgroundColor = event.color;
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px',
        padding: '2px 4px'
      }
    };
  };

  // Obtener color según tipo de evento
  const getColorByType = (eventType) => {
    switch (eventType) {
      case 'evento':
        return 'green';
      case 'santo_agustino':
        return 'red';
      case 'santoral':
        return 'blue';
      default:
        return 'blue';
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header con estadísticas */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Title level={2}>
            <CalendarOutlined /> Gestión de Calendario
          </Title>
        </Col>

        {stats && (
          <Col span={24}>
            <Row gutter={16}>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Total de Eventos"
                    value={stats.total_events}
                    prefix={<CalendarOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Días internacionales"
                    value={stats.by_type.find(s => s.event_type === 'evento')?.total || 0}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Santos Agustinos"
                    value={stats.by_type.find(s => s.event_type === 'santo_agustino')?.total || 0}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Calendario litúrgico"
                    value={stats.by_type.find(s => s.event_type === 'santoral')?.total || 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
            </Row>
          </Col>
        )}
      </Row>

      {/* Filtros y buscador */}
      <Card 
        size="small" 
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12} lg={8}>
            <Input
              placeholder="Buscar eventos..."
              prefix={<InfoCircleOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filtrar por tipo"
              value={filters.eventType}
              onChange={(value) => setFilters({ ...filters, eventType: value })}
            >
              <Select.Option value="all">
                📅 Todos los eventos
              </Select.Option>
              <Select.Option value="evento">
                🟢 Días internacionales
              </Select.Option>
              <Select.Option value="santo_agustino">
                🔴 Santos Agustinos
              </Select.Option>
              <Select.Option value="santoral">
                🔵 Calendario litúrgico
              </Select.Option>
            </Select>
          </Col>
          <Col xs={24} md={24} lg={8}>
            <Space className="cms-actions-inline" style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleSelectSlot({ start: new Date() })}
              >
                Nuevo Evento
              </Button>
              <Button
                icon={<ImportOutlined />}
                onClick={() => setImportOpen(true)}
              >
                Importar
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadEvents}
                loading={loading}
              >
                Recargar
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Leyenda de colores */}
      <Card 
        size="small" 
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: '8px 16px' }}
      >
        <Space size="large" wrap>
          <Text strong>Leyenda:</Text>
          <Tag color="green">🟢 Días internacionales</Tag>
          <Tag color="red">🔴 Santos Agustinos</Tag>
          <Tag color="blue">🔵 Calendario litúrgico</Tag>
        </Space>
      </Card>

      {/* Calendario */}
      <Card loading={loading}>
        <div style={{ height: 600 }}>
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            messages={messages}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            selectable
            eventPropGetter={eventStyleGetter}
            views={['month', 'week', 'day', 'agenda']}
            defaultView="month"
            popup
            date={currentDate}
            onNavigate={(date) => setCurrentDate(date)}
          />
        </div>
      </Card>

      {/* Modal de crear/editar evento */}
      <Modal
        title={editingEvent ? 'Editar Evento' : 'Nuevo Evento'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveEvent}
          initialValues={{
            event_type: 'santoral',
            color: 'blue',
            recurring: true
          }}
        >
          <Form.Item
            label="Título"
            name="title"
            rules={[{ required: true, message: 'El título es obligatorio' }]}
          >
            <Input placeholder="Ej: S. Juan Crisóstomo" />
          </Form.Item>

          <Form.Item
            label="Descripción"
            name="description"
          >
            <TextArea rows={3} placeholder="Descripción opcional del evento" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Fecha"
                name="date"
                rules={[{ required: true, message: 'La fecha es obligatoria' }]}
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Seleccionar fecha"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label="Tipo de Evento"
                name="event_type"
                rules={[{ required: true, message: 'El tipo es obligatorio' }]}
              >
                <Select 
                  placeholder="Seleccionar tipo"
                  onChange={(value) => {
                    form.setFieldsValue({ color: getColorByType(value) });
                  }}
                >
                  <Select.Option value="santoral">
                    <Tag color="blue">Calendario litúrgico</Tag>
                  </Select.Option>
                  <Select.Option value="evento">
                    <Tag color="green">Días internacionales</Tag>
                  </Select.Option>
                  <Select.Option value="santo_agustino">
                    <Tag color="red">Santo Agustino</Tag>
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Color"
                name="color"
                rules={[{ required: true }]}
              >
                <Select>
                  <Select.Option value="blue">🔵 Azul</Select.Option>
                  <Select.Option value="green">🟢 Verde</Select.Option>
                  <Select.Option value="red">🔴 Rojo</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  <Space>
                    Recurrente
                    <Tooltip title="Si está activado, el evento se repetirá cada año">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="recurring"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Vincular a Artículo (opcional)"
            name="article_id"
          >
            <Select
              placeholder="Seleccionar artículo"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {articles.map(article => (
                <Select.Option key={article.id} value={article.id}>
                  {article.title}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <Button onClick={() => setModalVisible(false)}>
                  Cancelar
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingEvent ? 'Actualizar' : 'Crear'}
                </Button>
              </Space>

              {editingEvent && (
                <Popconfirm
                  title="¿Eliminar este evento?"
                  description="Esta acción no se puede deshacer"
                  onConfirm={handleDeleteEvent}
                  okText="Sí, eliminar"
                  cancelText="Cancelar"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    Eliminar
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <CalendarImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => loadEvents()}
      />
    </div>
  );
};

export default CalendarPage;